import React, { useState } from 'react';
import { FiCode, FiDatabase, FiCloud, FiShield, FiCpu, FiUsers, FiSettings, FiTrendingUp, FiPlus, FiMinus, FiArrowRight } from 'react-icons/fi';
import Hero from '../components/Hero/Hero';
import About from '../components/About/About';
import Industries from '../components/Industries/Industries';
import Contact from '../components/Contact/Contact';
import '../pages/ServicesPage.css';

const HomePage = () => {
  const [activeService, setActiveService] = useState(0);

  const services = [
    {
      icon: <FiCode />,
      title: 'Software Development',
      shortDesc: 'Custom applications tailored to your needs',
      description: 'We build robust, scalable software solutions that drive business growth. From enterprise applications to mobile apps, our development team delivers excellence.',
      capabilities: [
        'Custom Enterprise Applications',
        'Mobile App Development (iOS & Android)',
        'Progressive Web Applications',
        'API Development & Integration',
        'Legacy System Modernization',
        'DevOps & CI/CD Implementation'
      ],
      process: ['Discovery', 'Design', 'Development', 'Testing', 'Deployment', 'Support']
    },
    {
      icon: <FiDatabase />,
      title: 'Data & Analytics',
      shortDesc: 'Transform data into actionable insights',
      description: 'Unlock the power of your data with our comprehensive analytics solutions. We help you make informed decisions through advanced data processing and visualization.',
      capabilities: [
        'Business Intelligence Solutions',
        'Data Warehouse Architecture',
        'Predictive Analytics & ML',
        'Real-time Data Processing',
        'Data Governance & Quality',
        'Custom Dashboard Development'
      ],
      process: ['Assessment', 'Strategy', 'Implementation', 'Integration', 'Analytics', 'Optimization']
    },
    {
      icon: <FiCloud />,
      title: 'Cloud Services',
      shortDesc: 'Accelerate your cloud journey',
      description: 'Navigate the cloud landscape with confidence. We provide end-to-end cloud services from migration to optimization across all major platforms.',
      capabilities: [
        'Cloud Migration & Strategy',
        'AWS, Azure, GCP Solutions',
        'Hybrid & Multi-Cloud Architecture',
        'Serverless Application Development',
        'Cloud Cost Optimization',
        'Disaster Recovery & Backup'
      ],
      process: ['Assessment', 'Planning', 'Migration', 'Optimization', 'Management', 'Innovation']
    },
    {
      icon: <FiShield />,
      title: 'Cybersecurity',
      shortDesc: 'Protect your digital assets',
      description: 'Comprehensive security solutions to safeguard your business from evolving threats. We implement robust security measures and ensure compliance.',
      capabilities: [
        'Security Assessment & Audits',
        'SIEM Implementation',
        'Penetration Testing',
        'Compliance Management',
        'Identity & Access Management',
        'Security Operations Center'
      ],
      process: ['Assess', 'Identify', 'Protect', 'Detect', 'Respond', 'Recover']
    },
    {
      icon: <FiCpu />,
      title: 'AI & Innovation',
      shortDesc: 'Next-gen technology solutions',
      description: 'Stay ahead with cutting-edge technologies. We help you leverage AI, ML, IoT, and blockchain to create innovative solutions that differentiate your business.',
      capabilities: [
        'Artificial Intelligence Solutions',
        'Machine Learning Models',
        'IoT Platform Development',
        'Blockchain Applications',
        'RPA Implementation',
        'Computer Vision & NLP'
      ],
      process: ['Ideation', 'POC', 'Development', 'Training', 'Deployment', 'Evolution']
    },
    {
      icon: <FiUsers />,
      title: 'IT Staff Augmentation',
      shortDesc: 'Scale your team on demand',
      description: 'Access top-tier technology talent to complement your team. We provide skilled professionals who integrate seamlessly with your organization.',
      capabilities: [
        'Dedicated Development Teams',
        'Project-based Resources',
        'Specialized Skill Hiring',
        'Remote Team Management',
        'Flexible Engagement Models',
        'Quick Onboarding Process'
      ],
      process: ['Requirements', 'Sourcing', 'Screening', 'Selection', 'Onboarding', 'Management']
    },
    {
      icon: <FiSettings />,
      title: 'Managed IT Services',
      shortDesc: '24/7 infrastructure management',
      description: 'Focus on your core business while we manage your IT infrastructure. Our managed services ensure optimal performance, security, and reliability.',
      capabilities: [
        '24/7 Infrastructure Monitoring',
        'Proactive Maintenance',
        'Help Desk Support',
        'Network Management',
        'Backup & Recovery',
        'Vendor Management'
      ],
      process: ['Onboard', 'Monitor', 'Maintain', 'Optimize', 'Report', 'Improve']
    },
    {
      icon: <FiTrendingUp />,
      title: 'IT Consulting',
      shortDesc: 'Strategic technology guidance',
      description: 'Strategic consulting to align technology with business objectives. We help you develop roadmaps and strategies that drive digital transformation.',
      capabilities: [
        'Digital Transformation Strategy',
        'Technology Roadmapping',
        'Enterprise Architecture',
        'Process Optimization',
        'Change Management',
        'Vendor Selection & Management'
      ],
      process: ['Discover', 'Analyze', 'Strategize', 'Plan', 'Execute', 'Transform']
    }
  ];

  return (
    <>
      {/* 1. HERO - Hook them */}
      <Hero />

      {/* 2. ABOUT - Establish trust */}
      <About />

      {/* 3. WHY CHOOSE US - Build credibility */}
      <section id="why-us" className="why-section section">
        <div className="container">
          <div className="why-grid">
            <div className="why-content">
              <span className="section-subtitle">Why Choose Us</span>
              <h2>Partnering for <span className="highlight">Success</span></h2>
              <p>We don't just deliver services; we build lasting partnerships that drive continuous value for your business.</p>

              <div className="why-points">
                <div className="why-point">
                  <span className="point-num">01</span>
                  <div>
                    <h4>Expert Teams</h4>
                    <p>Certified professionals with deep domain expertise</p>
                  </div>
                </div>
                <div className="why-point">
                  <span className="point-num">02</span>
                  <div>
                    <h4>Proven Methodology</h4>
                    <p>Battle-tested processes that ensure quality delivery</p>
                  </div>
                </div>
                <div className="why-point">
                  <span className="point-num">03</span>
                  <div>
                    <h4>Flexible Engagement</h4>
                    <p>Customizable models to fit your unique needs</p>
                  </div>
                </div>
                <div className="why-point">
                  <span className="point-num">04</span>
                  <div>
                    <h4>24/7 Support</h4>
                    <p>Round-the-clock assistance when you need it</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="why-visual">
              <div className="visual-card card-a">
                <span className="big-number">10+</span>
                <span className="card-label">Years Experience</span>
              </div>
              <div className="visual-card card-b">
                <span className="big-number">50+</span>
                <span className="card-label">Happy Clients</span>
              </div>
              <div className="visual-card card-c">
                <span className="big-number">100%</span>
                <span className="card-label">Commitment</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4. SERVICES - Show how we help */}
      <section id="services" className="services-interactive section">
        <div className="container">
          <div className="section-header">
            <span className="section-subtitle">What We Offer</span>
            <h2 className="section-title">Our Services</h2>
            <p className="section-description">End-to-end technology solutions designed to accelerate your digital journey</p>
          </div>
          <div className="services-layout">
            {/* Left: Accordion List */}
            <div className="services-accordion">
              {services.map((service, index) => (
                <div
                  key={index}
                  className={`accordion-item ${activeService === index ? 'active' : ''}`}
                  onClick={() => setActiveService(activeService === index ? -1 : index)}
                >
                  <div className="accordion-header">
                    <div className="accordion-icon">{service.icon}</div>
                    <div className="accordion-info">
                      <h3>{service.title}</h3>
                      <p>{service.shortDesc}</p>
                    </div>
                    <div className="accordion-toggle">
                      {activeService === index ? <FiMinus /> : <FiPlus />}
                    </div>
                  </div>

                  {/* Mobile: Expandable content inside accordion */}
                  <div className={`accordion-content ${activeService === index ? 'expanded' : ''}`}>
                    <div className="accordion-details">
                      <p className="details-description">{service.description}</p>

                      <div className="capabilities-section">
                        <h4>Key Capabilities</h4>
                        <div className="capabilities-grid">
                          {service.capabilities.map((cap, idx) => (
                            <div key={idx} className="capability-item">
                              <FiArrowRight />
                              <span>{cap}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="process-section">
                        <h4>Our Process</h4>
                        <div className="process-steps">
                          {service.process.map((step, idx) => (
                            <div key={idx} className="process-step">
                              <span className="step-number">{idx + 1}</span>
                              <span className="step-name">{step}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      <a href="#contact" className="details-cta" onClick={(e) => e.stopPropagation()}>
                        Discuss This Service <FiArrowRight />
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Right: Service Details (Desktop only) */}
            <div className="service-details-panel">
              <div className="details-content">
                <div className="details-icon">{services[activeService >= 0 ? activeService : 0].icon}</div>
                <h2>{services[activeService >= 0 ? activeService : 0].title}</h2>
                <p className="details-description">{services[activeService >= 0 ? activeService : 0].description}</p>

                <div className="capabilities-section">
                  <h4>Key Capabilities</h4>
                  <div className="capabilities-grid">
                    {services[activeService >= 0 ? activeService : 0].capabilities.map((cap, idx) => (
                      <div key={idx} className="capability-item">
                        <FiArrowRight />
                        <span>{cap}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="process-section">
                  <h4>Our Process</h4>
                  <div className="process-steps">
                    {services[activeService >= 0 ? activeService : 0].process.map((step, idx) => (
                      <div key={idx} className="process-step">
                        <span className="step-number">{idx + 1}</span>
                        <span className="step-name">{step}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <a href="#contact" className="details-cta">
                  Discuss This Service <FiArrowRight />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 5. INDUSTRIES - Social proof */}
      <Industries />

      {/* 6. CONTACT - Take action */}
      <Contact />
    </>
  );
};

export default HomePage;
